import Immutable from 'immutable';
import {
  UPDATE_CARDS,
  UPDATE_GOALS,
  UPDATE_PERIOD,
  UPDATE_SCORE,
  UPDATE_STATS,
  UPDATE_TIME
} from '../livescore/actions';
import { EVENT_IN_PLAY_CHANGE } from '../pds/actions';
import { IS_LOADING, SET_EVENTS } from './actions';

export const Entities = new Immutable.Record({
  competitions: new Immutable.Map(),
  events: new Immutable.Map(),
  markets: new Immutable.Map(),
  selections: new Immutable.Map()
});

export const Competition = new Immutable.Record({
  competitionId: '',
  competitionName: '',
  events: new Immutable.List()
});

export const FootballCardOrGoal = new Immutable.Record({
  type: '',
  time: '',
  period: '',
  playerName: '',
  playerNumber: ''
});

export const TeamStatsItem = new Immutable.Record({
  home: 0,
  away: 0
});

export const FootballStats = new Immutable.Record({
  shotsOnTarget: new TeamStatsItem(),
  shotsOnWoodwork: new TeamStatsItem(),
  corners: new TeamStatsItem(),
  goalKicks: new TeamStatsItem(),
  shotsOffTarget: new TeamStatsItem(),
  attacks: new TeamStatsItem(),
  dangerousAttacks: new TeamStatsItem(),
  freeKicks: new TeamStatsItem(),
  throwIns: new TeamStatsItem(),
  possession: new TeamStatsItem(),
  penalties: new TeamStatsItem(),
  redCards: new TeamStatsItem(),
  yellowCards: new TeamStatsItem(),
  possessionTime: new TeamStatsItem()
});

export const Event = new Immutable.Record({
  competitionId: '',
  eventId: '',
  eventName: '',
  eventStartDateTime: '',
  eventStartTime: '',
  eventInPlay: false,
  eventIsFinished: false,
  eventLiveTime: '',
  eventPeriod: '',
  scoreHome: '',
  scoreAway: '',
  scoreLabel: '',
  subScoreHome: '',
  subScoreAway: '',
  subScoreLabel: '',
  markets: new Immutable.List(),
  cardsHome: null,
  cardsAway: null,
  goalsHome: null,
  goalsAway: null,
  totalStats: null
});

export const Market = new Immutable.Record({
  competitionId: '',
  eventId: '',
  marketId: '',
  marketName: '',
  selections: new Immutable.List()
});

export const Selection = new Immutable.Record({
  competitionId: '',
  eventId: '',
  marketId: '',
  selectionId: 0,
  selectionName: '',
  selectionPriceNum: 1,
  selectionPriceDen: 1
});

export const InitialState = new Immutable.Record({
  entities: new Entities(),
  competitionIds: new Immutable.List(),
  isLoading: false
});

const mapCompetitions = (event, index, events) => [event.competitionId, new Competition({
  competitionId: event.competitionId,
  competitionName: event.competitionName,
  events: new Immutable.List(
    events.filter(e => e.competitionId === event.competitionId).map(e => e.id)
  )
})];

const mapEvents = event => [event.id, new Event({
  competitionId: event.competitionId,
  eventId: event.id,
  eventName: event.name,
  eventStartDateTime: event.startDateTime,
  eventStartTime: event.startTime,
  markets: new Immutable.List(event.markets.map(market => market.id))
})];

const mapEventMarkets = event => market => [market.id, new Market({
  competitionId: event.competitionId,
  eventId: event.id,
  marketId: market.id,
  marketName: market.name,
  selections: new Immutable.List(market.selections.map(selection => selection.id))
})];

const mapEventMarketSelections = (event, market) => selection => [selection.id, new Selection({
  competitionId: event.competitionId,
  eventId: event.id,
  marketId: market.id,
  selectionId: selection.id,
  selectionName: selection.name,
  selectionPriceNum: selection.currentPriceNum,
  selectionPriceDen: selection.currentPriceDen
})];

const reduceEventsToMarkets = (markets, event) => markets.concat(
  event.markets.map(mapEventMarkets(event))
);

const reduceEventMarketsToSelections = event => (selections, market) => selections.concat(
  market.selections.map(mapEventMarketSelections(event, market))
);

const reduceEventsToSelections = (selections, event) => selections.concat(
  event.markets.reduce(reduceEventMarketsToSelections(event), [])
);

const entitiesToUtf = str => str.replace(/&#(\d+);/g, (match, p1) => String.fromCharCode(p1));

const mapFootballGoals = goal => new FootballCardOrGoal({
  type: goal.type,
  time: goal.timeFormatted,
  period: goal.periodFormatted,
  playerName: (goal.scorer && entitiesToUtf(goal.scorer.name)) || '',
  playerNumber: (goal.scorer && goal.scorer.number) || ''
});

const mapFootballCards = card => new FootballCardOrGoal({
  type: card.type,
  time: card.timeFormatted,
  period: card.periodFormatted,
  playerName: (card.player && entitiesToUtf(card.player.name)) || '',
  playerNumber: (card.player && card.player.number) || ''
});

export const liveEventsReducer = (state, action) => {
  const { type, eventId } = action;
  if (!state.hasIn(['entities', 'events', eventId])) {
    return state;
  }
  switch (type) {
    case EVENT_IN_PLAY_CHANGE: {
      const { isInPlay, isPostMatch } = action;
      return state.mergeIn(['entities', 'events', eventId], {
        eventInPlay: isInPlay,
        eventIsFinished: isPostMatch
      });
    }
    case UPDATE_TIME: {
      const { time } = action;
      return state.setIn(['entities', 'events', eventId, 'eventLiveTime'], time);
    }
    case UPDATE_PERIOD: {
      const { period } = action;
      return state.setIn(['entities', 'events', eventId, 'eventPeriod'], period);
    }
    case UPDATE_SCORE: {
      const { score } = action;
      const definedScore = Object.entries(score).filter(([, value]) => value !== undefined);
      return state.mergeIn(['entities', 'events', eventId], definedScore);
    }
    case UPDATE_GOALS: {
      const { goals } = action;
      const goalsHome = new Immutable.List(
        goals.filter(g => g.teamType === 'HOME').map(mapFootballGoals)
      );
      const goalsAway = new Immutable.List(
        goals.filter(g => g.teamType === 'AWAY').map(mapFootballGoals)
      );
      return state.mergeIn(['entities', 'events', eventId], { goalsHome, goalsAway });
    }
    case UPDATE_CARDS: {
      const { cards } = action;
      const cardsHome = new Immutable.List(
        cards.filter(c => c.teamType === 'HOME').map(mapFootballCards)
      );
      const cardsAway = new Immutable.List(
        cards.filter(c => c.teamType === 'AWAY').map(mapFootballCards)
      );
      return state.mergeIn(['entities', 'events', eventId], { cardsHome, cardsAway });
    }
    case UPDATE_STATS: {
      const { total } = action;
      const entries = Object.entries(total).map(([f, v]) => [f, new TeamStatsItem(v)]);
      return state.setIn(['entities', 'events', eventId, 'totalStats'], new FootballStats(entries));
    }
    default: {
      return state;
    }
  }
};

export default (state = new InitialState(), action) => {
  const { type } = action;
  switch (type) {
    case SET_EVENTS: {
      const { events } = action;
      const competitionsEntities = events.map(mapCompetitions);
      const eventsEntities = events.map(mapEvents);
      const marketsEntities = events.reduce(reduceEventsToMarkets, []);
      const selectionsEntities = events.reduce(reduceEventsToSelections, []);
      const competitionIds = competitionsEntities.map(c => c[1].competitionId);

      return state.merge({
        competitionIds: new Immutable.List(competitionIds).toOrderedSet().toList(),
        entities: new Entities({
          competitions: new Immutable.Map(competitionsEntities),
          events: new Immutable.Map(eventsEntities),
          markets: new Immutable.Map(marketsEntities),
          selections: new Immutable.Map(selectionsEntities)
        })
      });
    }
    case IS_LOADING: {
      return state.set('isLoading', action.loading);
    }
    default: {
      return liveEventsReducer(state, action);
    }
  }
};
